import type { CampaignStatsResponse, MetaCampaignStatsRow, WhatsAppCampaignStatsRow } from 'services/api/leads.api';
import type { MetaCampaignsAccountBlock, MetaCampaignWithInsights } from 'services/api/metaInsights.api';

import type { CampaignRow, CampaignStatus, MetaCampaignRowSource, MetaInsightsEnrichment } from '../types';

const parseNumeric = (value: string | number | undefined | null): number | null => {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const ratio = (numerator: number, denominator: number | null): number | null => {
  if (!denominator || denominator <= 0) return null;
  return numerator / denominator;
};

const crmRateFrom = (row: { totalLeads: number; leadsInCrm: number }): number | null => {
  if (row.totalLeads <= 0) return null;
  return row.leadsInCrm / row.totalLeads;
};

/** WhatsApp stat row → CampaignRow. Spend/impressions/clicks are N/A for WhatsApp. */
export const whatsAppStatToRow = (stat: WhatsAppCampaignStatsRow): CampaignRow => {
  const totalLeads = stat.totalLeads ?? 0;
  const leadsInCrm = stat.leadsUploadedToCRM ?? 0;
  const leadsContacted = stat.leadsWithClientResponses ?? 0;

  return {
    id: `wa:${stat.whatsAppCampaignId}`,
    channel: 'whatsapp',
    campaignName: stat.whatsAppCampaignName ?? `Campaña ${stat.whatsAppCampaignId}`,
    accountName: null,
    status: 'unknown',
    objective: null,
    spend: null,
    totalLeads,
    leadsContacted,
    leadsActive: null,
    leadsInCrm,
    impressions: null,
    clicks: null,
    ctr: null,
    cpc: null,
    costPerContactedLead: null,
    costPerCrmLead: null,
    crmRate: crmRateFrom({ totalLeads, leadsInCrm }),
    currency: null,
    raw: stat,
  };
};

// Meta only surfaces ACTIVE/PAUSED in the panel; anything else maps to paused so
// the UI doesn't have to handle states it can't render.
const mapStatusFromMeta = (status: string | undefined): CampaignStatus => (status === 'ACTIVE' ? 'active' : 'paused');

interface MetaCampaignGroup {
  metaCampaignId: string;
  metaCampaignName: string;
  adAccountId: string;
  adAccountName: string | null;
  currency: string | null;
  metaConnectionId: number | null;
  insightsCampaign: MetaCampaignWithInsights | null;
  stats: {
    totalLeads: number;
    leadsWithMessagesSent: number;
    leadsWithClientResponses: number;
    leadsWithHighMessageCount: number;
    leadsUploadedToCRM: number;
    sampleAdRow: MetaCampaignStatsRow;
  } | null;
}

/**
 * Merge meta insights blocks (per account, per campaign) with the lead stats per MetaAd.
 * Meta stats rows come keyed by metaAdId; we group them by metaCampaignId and enrich with
 * insights payload so the table shows one row per Meta campaign.
 */
export const buildMetaCampaignRows = (
  metaStats: MetaCampaignStatsRow[],
  insightBlocks: MetaCampaignsAccountBlock[]
): CampaignRow[] => {
  const groups = new Map<string, MetaCampaignGroup>();

  insightBlocks.forEach((block) => {
    const adAccountId = block.metaAdAccount.metaAdAccountId;
    const adAccountName = block.metaAdAccount.name;
    const currency = block.metaAdAccount.currency ?? null;
    const metaConnectionId = block.metaConnectionId;

    block.campaigns.forEach((campaign) => {
      if (!groups.has(campaign.id)) {
        groups.set(campaign.id, {
          metaCampaignId: campaign.id,
          metaCampaignName: campaign.name,
          adAccountId,
          adAccountName,
          currency,
          metaConnectionId,
          insightsCampaign: campaign,
          stats: null,
        });
      }
    });
  });

  metaStats.forEach((stat) => {
    const campaignKey = stat.metaCampaignId;
    if (!campaignKey) return;

    const existing =
      groups.get(campaignKey) ??
      ({
        metaCampaignId: campaignKey,
        metaCampaignName: stat.metaCampaignName ?? stat.metaAdName ?? campaignKey,
        adAccountId: 'unknown',
        adAccountName: null,
        currency: null,
        metaConnectionId: null,
        insightsCampaign: null,
        stats: null,
      } satisfies MetaCampaignGroup);

    const prior = existing.stats ?? {
      totalLeads: 0,
      leadsWithMessagesSent: 0,
      leadsWithClientResponses: 0,
      leadsWithHighMessageCount: 0,
      leadsUploadedToCRM: 0,
      sampleAdRow: stat,
    };

    existing.stats = {
      totalLeads: prior.totalLeads + (stat.totalLeads ?? 0),
      leadsWithMessagesSent: prior.leadsWithMessagesSent + (stat.leadsWithMessagesSent ?? 0),
      leadsWithClientResponses: prior.leadsWithClientResponses + (stat.leadsWithClientResponses ?? 0),
      leadsWithHighMessageCount: prior.leadsWithHighMessageCount + (stat.leadsWithHighMessageCount ?? 0),
      leadsUploadedToCRM: prior.leadsUploadedToCRM + (stat.leadsUploadedToCRM ?? 0),
      sampleAdRow: prior.sampleAdRow,
    };

    groups.set(campaignKey, existing);
  });

  const rows: CampaignRow[] = [];
  groups.forEach((group) => {
    const insights = group.insightsCampaign?.insights ?? null;
    const spend = insights ? parseNumeric(insights.spend ?? null) : null;
    const impressions = insights ? parseNumeric(insights.impressions ?? null) : null;
    const reach = insights ? parseNumeric(insights.reach ?? null) : null;
    const clicks = insights ? parseNumeric(insights.clicks ?? null) : null;
    const ctr = insights ? parseNumeric(insights.ctr ?? null) : null;
    const cpc = insights ? parseNumeric(insights.cpc ?? null) : null;

    const totalLeads = group.stats?.totalLeads ?? 0;
    const leadsContacted = group.stats?.leadsWithClientResponses ?? 0;
    const leadsActive = group.stats?.leadsWithHighMessageCount ?? 0;
    const leadsInCrm = group.stats?.leadsUploadedToCRM ?? 0;

    const costPerContactedLead = ratio(spend ?? 0, leadsContacted);
    const costPerCrmLead = ratio(spend ?? 0, leadsInCrm);

    const enrichment: MetaInsightsEnrichment | null = group.insightsCampaign
      ? {
          metaCampaignId: group.metaCampaignId,
          metaConnectionId: group.metaConnectionId,
          currency: group.currency,
          status: group.insightsCampaign.status,
          isActive: group.insightsCampaign.isActive,
          startTime: group.insightsCampaign.startTime,
          objective: group.insightsCampaign.objective,
          spend,
          impressions,
          reach,
          clicks,
          ctr,
          cpc,
        }
      : null;

    const status = group.insightsCampaign ? mapStatusFromMeta(group.insightsCampaign.status) : 'unknown';

    const rawSource: MetaCampaignRowSource = {
      adAccountId: group.adAccountId,
      adAccountName: group.adAccountName,
      stats: group.stats?.sampleAdRow ?? {
        metaAdId: group.metaCampaignId,
        metaAdName: group.metaCampaignName,
        metaAdSetId: null,
        metaAdSetName: null,
        metaCampaignId: group.metaCampaignId,
        metaCampaignName: group.metaCampaignName,
        totalLeads,
        leadsWithMessagesSent: 0,
        leadsWithClientResponses: leadsContacted,
        leadsWithHighMessageCount: leadsActive,
        leadsUploadedToCRM: leadsInCrm,
      },
      insights: group.insightsCampaign,
      enrichment,
    };

    rows.push({
      id: `meta:${group.metaCampaignId}`,
      channel: 'meta',
      campaignName: group.metaCampaignName,
      accountName: group.adAccountName,
      status,
      objective: group.insightsCampaign?.objective ?? null,
      spend,
      totalLeads,
      leadsContacted,
      leadsActive,
      leadsInCrm,
      impressions,
      clicks,
      ctr,
      cpc,
      costPerContactedLead,
      costPerCrmLead,
      crmRate: crmRateFrom({ totalLeads, leadsInCrm }),
      currency: group.currency,
      raw: rawSource,
    });

    // `reach` captured but unused at row level — kept in enrichment for drawer display.
    void reach;
  });

  return rows;
};

export interface BuildCampaignRowsInput {
  campaignStats: CampaignStatsResponse | undefined;
  metaInsightBlocks: MetaCampaignsAccountBlock[];
  channels: string[];
}

/**
 * Top-level mapper: given the lead-stats response and the meta insights blocks (optional,
 * only present when meta is an active channel), produce the unified rows array.
 */
export const buildCampaignRows = ({
  campaignStats,
  metaInsightBlocks,
  channels,
}: BuildCampaignRowsInput): CampaignRow[] => {
  if (!campaignStats) return [];

  const rows: CampaignRow[] = [];

  if (channels.includes('whatsapp')) {
    campaignStats.whatsAppCampaignStats.forEach((stat) => {
      rows.push(whatsAppStatToRow(stat));
    });
  }

  if (channels.includes('meta')) {
    rows.push(...buildMetaCampaignRows(campaignStats.metaCampaignStats, metaInsightBlocks));
  }

  return rows;
};
